<template>
  <div class="choice-step">
    <div class="choice-content">
      <!-- Title Section -->
      <div class="title-section">
        <h2 class="step-title">{{ step.title }}</h2>
        <p v-if="step.subtitle" class="step-subtitle">{{ step.subtitle }}</p>
      </div>

      <!-- Styles Selection Dropdown -->
      <div class="styles-dropdown">
        <!-- Dropdown Header -->
        <div class="dropdown-header">
          <h3 class="dropdown-title">Выберите стили для оценки</h3>
          <div v-if="totalPhotosCount > 0" class="total-photos-badge" >
            {{ totalPhotosCount }} картинок
          </div>
        </div>

        <!-- Styles List -->
        <div class="styles-list">
          <div
            v-for="option in step.options"
            :key="option.value"
            class="style-item"
            :class="{ selected: isStyleSelected(option.value) }"
            @click="toggleStyle(option.value)"
          >
            <!-- Checkbox -->
            <div class="style-checkbox">
              <input
                type="checkbox"
                :checked="isStyleSelected(option.value)"
                @click.stop
                @change="toggleStyle(option.value)"
                class="checkbox-input"
              />
            </div>

            <!-- Style Image -->
            <img
              :src="getStyleImage(option)"
              :alt="option.label"
              class="style-image"
            />

            <!-- Text Block -->
            <div class="style-text-block">
              <div class="style-name">{{ option.label }}</div>
              <div class="style-progress" v-if="option.percentage !== undefined">
                {{ formatProgress(option.percentage) }}
              </div>
            </div>

            <!-- Photos Count Badge -->
            <div class="photos-count-badge" v-if="option.photosCount !== undefined">
              +{{ option.photosCount }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useTasteClarificationModal } from '@/features/taste/store'
import type { TasteStep } from '@/features/taste/types/taste-config'
import { getStyleImagePath } from '@/features/taste/utils/style-images'
import { mapStyleImageFromBackend } from '@/utils/style-image-mapper'

interface Props {
  step: TasteStep & { type: 'choice' }
}

const props = defineProps<Props>()
const tasteStore = useTasteClarificationModal()

const selectedStyles = computed(() => {
  const value = tasteStore.tasteModal.tasteData[props.step.id]
  if (props.step.multiSelect) {
    return Array.isArray(value) ? value : []
  }
  return value ? [value] : []
})

const isStyleSelected = (styleValue: string): boolean => {
  return Array.isArray(selectedStyles.value) && selectedStyles.value.includes(styleValue)
}

const totalPhotosCount = computed(() => {
  if (!Array.isArray(selectedStyles.value)) return 0
  return props.step.options
    .filter(option => selectedStyles.value.includes(option.value))
    .reduce((sum, option) => sum + (option.photosCount || 0), 0)
})

const canContinue = computed(() => {
  if (!props.step.multiSelect) return true
  // Если шаг не обязателен, можно продолжить в любом случае
  if (!props.step.required) return true
  // Если шаг обязателен, нужно выбрать хотя бы одну опцию
  return Array.isArray(selectedStyles.value) && selectedStyles.value.length > 0
})

function toggleStyle(styleValue: string) {
  if (!props.step.multiSelect) {
    // Для одиночного выбора просто сохраняем значение
    tasteStore.updateTasteData(props.step.id, styleValue)
    return
  }

  // Для множественного выбора добавляем/удаляем из массива
  // Читаем текущие значения напрямую из store для гарантии актуальности
  const currentValue = tasteStore.tasteModal.tasteData[props.step.id]
  const currentValues = Array.isArray(currentValue) ? [...currentValue] : []
  const index = currentValues.indexOf(styleValue)
  
  if (index > -1) {
    currentValues.splice(index, 1)
  } else {
    currentValues.push(styleValue)
  }

  tasteStore.updateTasteData(props.step.id, currentValues)
}

function getStyleImage(option: { value: string; image?: string; code?: string }): string {
  // Если в опции есть изображение, применяем маппинг из бэкенда
  if (option.image) {
    // Применяем маппинг, используя code если есть, иначе value как потенциальный код
    return mapStyleImageFromBackend({
      code: option.code || option.value,
      image: option.image
    })
  }
  // Иначе используем путь из утилиты
  return getStyleImagePath(option.value)
}

function formatProgress(percentage: number): string {
  return `Прогресс: ${percentage}%`
}

// Автоматически выбираем стили из style-selection-step при монтировании
// Только для шага style-evaluation-choice
onMounted(() => {
  // Проверяем, что это шаг style-evaluation-choice
  if (props.step.id !== 'style-evaluation-choice') return
  
  // Находим шаг style_selection в конфигурации
  const styleSelectionStep = tasteStore.steps.find(step => step.type === 'style_selection')
  
  if (!styleSelectionStep) return
  
  // Получаем выбранные стили из tasteData для шага style_selection
  const selectedStylesFromSelection = tasteStore.tasteModal.tasteData[styleSelectionStep.id]
  
  if (!selectedStylesFromSelection || !Array.isArray(selectedStylesFromSelection)) return
  
  // Нормализуем ID к числам
  const selectedStyleIdsFromSelection = selectedStylesFromSelection
    .map((id: any) => typeof id === 'number' ? id : parseInt(id, 10))
    .filter((id: any) => !isNaN(id))
  
  if (selectedStyleIdsFromSelection.length === 0) return
  
  // Находим опции из step.options, которые соответствуют выбранным на style-selection-step
  // option.value - это строка (ID стиля как строка)
  const optionsToSelect = props.step.options
    .filter(option => {
      const optionId = parseInt(option.value, 10)
      return !isNaN(optionId) && selectedStyleIdsFromSelection.includes(optionId)
    })
    .map(option => option.value)
  
  if (optionsToSelect.length === 0) return
  
  // Проверяем, есть ли уже сохраненные данные для текущего шага
  const currentData = tasteStore.tasteModal.tasteData[props.step.id]
  
  // Если данных еще нет или массив пустой, автоматически выбираем все найденные опции
  if (!currentData || (Array.isArray(currentData) && currentData.length === 0)) {
    tasteStore.updateTasteData(props.step.id, optionsToSelect)
  }
})
</script>

<style scoped>
.choice-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 40px 20px;
  text-align: center;
}

.choice-content {
  max-width: 500px;
  width: 100%;
}

.title-section {
  margin-bottom: 24px;
  text-align: left;
}

.step-title {
  font-size: 24px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0 0 12px 0;
}

.step-subtitle {
  font-size: 16px;
  color: #666;
  line-height: 1.5;
  margin: 0;
}

.styles-dropdown {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: rgba(110, 133, 203, 0.08);
  border-radius: 20px;
  margin-bottom: 24px;
}

.dropdown-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.dropdown-title {
  font-size: 36px;
  font-weight: 500;
  line-height: 1.2;
  color: #232F45;
  margin: 0;
}

.total-photos-badge {
  padding: 8px;
  border-radius: 13.33px;
  background: #232F45;
  color: white;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.21;
  white-space: nowrap;
}

.styles-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.style-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
}

.style-item:last-child {
  margin-bottom: 16px;
}

.style-item:hover {
  background: rgba(255, 255, 255, 0.9);
}

.style-checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.checkbox-input {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 2px solid rgba(35, 47, 69, 0.2);
  background: white;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  position: relative;
}

.checkbox-input:checked {
  background: #232F45;
  border-color: #232F45;
}

.checkbox-input:checked::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 12px;
  font-weight: bold;
}

.style-image {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
}

.style-text-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.style-name {
  text-align: left;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  color: #232F45;
}

.style-progress {
  text-align: left;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: rgba(35, 47, 69, 0.8);
}

.photos-count-badge {
  padding: 8px;
  border-radius: 20px;
  background: #232F45;
  color: white;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.21;
  white-space: nowrap;
  flex-shrink: 0;
}


@media (min-width: 393px) and (max-width: 767px) {
  .choice-step {
    padding: 0;
    align-self: stretch;
  }

  .choice-content {
    max-width: none;
    width: 100%;
    padding: 0 20px 20px;
    gap: 20px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  }

  .title-section {
    margin-bottom: 0;
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
    align-self: stretch;
  }

  .step-title {
    font-size: 32px;
    line-height: 1.09;
    font-weight: 700;
    color: #232F45;
    text-align: left;
    margin: 0;
  }

  .step-subtitle {
    font-size: 15px;
    line-height: 1.3;
    color: rgba(35, 47, 69, 0.8);
    letter-spacing: -0.03em;
  }

  .styles-dropdown {
    align-self: stretch;
    margin-bottom: 0;
    padding:16px 16px 0 16px;
  }

  .dropdown-header {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 12px;
  }

  .dropdown-title {
    font-size: 24px;
    font-weight: 600;
    line-height: 1.09;
    text-align: left;
    letter-spacing: -0.03em;
  }

  .total-photos-badge {
    padding:4px;
    font-weight: 500;
    font-size: 12px;
  }

  .styles-list{
    height:412px;
    overflow-y: auto;
  }
}

@media (min-width: 768px) and (max-width: 1023px) {
  .choice-step {
    padding: 0;
    align-self: stretch;
  }

  .choice-content {
    max-width: none;
    width: 100%;
    padding: 0 20px 20px;
    gap: 20px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  }

  .title-section {
    margin-bottom: 0;
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: 0;
    align-items: flex-start;
    align-self: stretch;
  }

  .step-title {
    font-size: 32px;
    line-height: 1.09;
    font-weight: 700;
    color: #232F45;
    text-align: left;
    margin: 0;
  }

  .step-subtitle {
    font-size: 15px;
    line-height: 1.3;
    color: rgba(35, 47, 69, 0.8);
  }

  .styles-dropdown {
    align-self: stretch;
    margin-bottom: 0;
    padding:16px 16px 0 16px;
  }

  .styles-list {
    height:464px;
  }

  .dropdown-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .total-photos-badge {
    padding:4px;
    font-weight: 500;
  }

  .dropdown-title {
    text-align: left;
    font-size: 24px;
    font-weight: 600;
    line-height: 1.09;
  }
}

@media (min-width: 1024px) and (max-width: 1439px) {
  .choice-step {
    padding: 0;
  }

  .choice-content {
    max-width: none;
    width: 100%;
    padding: 40px;
    gap: 40px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .styles-list {
    height:311px;
    overflow-y: auto;
  }

  .title-section {
    margin-bottom: 0;
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
    align-self: stretch;
  }


  .step-title {
    font-size: 48px;
    line-height: 1.2;
    font-weight: 700;
    color: #232F45;
    text-align: left;
    margin: 0;
  }

  .step-subtitle {
    font-size: 20px;
    line-height: 1.3;
    color: rgba(35, 47, 69, 0.8);
  }

  .styles-dropdown {
    align-self: stretch;
    margin-bottom: 0;
    padding:16px 16px 0 16px;
  }

  .dropdown-title {
    font-size: 36px;
    font-weight: 500;
    line-height: 1.2;
  }
}

@media (min-width: 1440px) and (max-width: 1919px) {
  .choice-step {
    padding: 0;
  }

  .choice-content {
    max-width: none;
    width: 100%;
    padding: 40px;
    gap: 40px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .title-section {
    margin-bottom: 0;
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
    align-self: stretch;
  }

  .step-title {
    font-size: 48px;
    line-height: 1.2;
    font-weight: 700;
    color: #232F45;
    text-align: center;
    margin: 0;
  }

  .styles-list {
    height:339px;
    overflow-y: auto;
  }

  .step-subtitle {
    font-size: 20px;
    line-height: 1.3;
    color: rgba(35, 47, 69, 0.8);
  }

  .styles-dropdown {
    align-self: stretch;
    padding:16px 16px 0 16px;
    margin-bottom: 0;
  }

  .dropdown-title {
    font-size: 36px;
    font-weight: 500;
    line-height: 1.2;
  }
}

@media (min-width: 1920px) {
  .choice-step {
    padding: 0;
  }

  .choice-content {
    max-width: none;
    width: 100%;
    padding: 40px 120px;
    gap: 40px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .title-section {
    margin-bottom: 0;
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
    align-self: stretch;
  }

  .step-title {
    font-size: 48px;
    line-height: 1.2;
    font-weight: 700;
    color: #232F45;
    text-align: center;
    margin: 0;
  }

  .step-subtitle {
    font-size: 20px;
    line-height: 1.3;
    color: rgba(35, 47, 69, 0.8);
  }

  .styles-dropdown {
    align-self: stretch;
    margin-bottom: 0;
    padding:16px 16px 0 16px;
  }

  .styles-list {
    height:361px;
    overflow-y: auto;
  }

  .dropdown-title {
    font-size: 36px;
    font-weight: 500;
    line-height: 1.2;
  }
}

@media (max-width: 480px) {




  .style-name {
    font-size: 16px;
  }

  .style-progress {
    font-size: 12px;
  }

  .photos-count-badge {
    font-size: 14px;
    padding: 6px;
  }
}
</style>

